/**
 * Starting numbers for Canada's largest housing markets, so a user does not have to go and look up
 * a condo price, a rent and a property tax rate before the calculator can tell them anything.
 *
 * These are dated snapshots, not a live feed. Every figure carries its source and the month it was
 * published, and the UI shows that date, because a stale number presented as current is worse than
 * no number at all. Refresh them roughly once a year.
 *
 * A preset only fills the four fields that are genuinely local. Everything else — rate, down
 * payment, horizon — stays as the user set it.
 */

export interface CityPreset {
  id: string;
  name: string;
  province: string;
  /** Average condo/apartment sale price. */
  houseCost: number;
  /** Average rent for a two-bedroom, the closest like-for-like to an average condo. */
  monthlyRent: number;
  /** Effective annual rate against market value, which is what the calculator applies. */
  propertyTaxPercent: number;
  condoFees: number;
  /** Anything about this city a reader would otherwise get wrong. */
  note?: string;
}

export const CITY_PRESET_AS_OF = 'Prices Aug–Sep 2026, rents Oct 2025, tax rates 2026';

export const CITY_PRESET_SOURCE =
  'Condo prices: WOWA city market reports (Aug–Sep 2026). ' +
  'Rents: CMHC average two-bedroom purpose-built rent (Oct 2025), as published by WOWA. ' +
  'Property tax: 2026 residential rates from WOWA. ' +
  'Condo fees: $300/month is a national estimate, not a city figure — adjust it if you know the building.';

/** A national estimate; per-city condo fee data is not published consistently. */
const ESTIMATED_CONDO_FEES = 300;

export const CITY_PRESETS: CityPreset[] = [
  {
    id: 'toronto',
    name: 'Toronto',
    province: 'ON',
    houseCost: 605257,
    monthlyRent: 2046,
    propertyTaxPercent: 0.77,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'Toronto charges a municipal land transfer tax on top of the Ontario one, so closing costs run higher here than anywhere else in Canada.'
  },
  {
    id: 'vancouver',
    name: 'Vancouver',
    province: 'BC',
    houseCost: 759995,
    monthlyRent: 2363,
    propertyTaxPercent: 0.34,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'The lowest property tax rate of any major Canadian city, against the highest prices.'
  },
  {
    id: 'montreal',
    name: 'Montreal',
    province: 'QC',
    houseCost: 430000,
    monthlyRent: 1346,
    propertyTaxPercent: 0.65,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'Quebec has no land transfer tax rebate for first-time buyers, but it does have the cheapest rents of any major market.'
  },
  {
    id: 'calgary',
    name: 'Calgary',
    province: 'AB',
    houseCost: 318759,
    monthlyRent: 1914,
    propertyTaxPercent: 0.66,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'Alberta charges no land transfer tax, only small registration fees.'
  },
  {
    id: 'edmonton',
    name: 'Edmonton',
    province: 'AB',
    houseCost: 208672,
    monthlyRent: 1603,
    propertyTaxPercent: 1.04,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'The cheapest condos of any major market, which makes the rent-versus-buy maths unusually favourable to buying.'
  },
  {
    id: 'ottawa',
    name: 'Ottawa',
    province: 'ON',
    houseCost: 426930,
    monthlyRent: 1926,
    propertyTaxPercent: 1.27,
    condoFees: ESTIMATED_CONDO_FEES
  },
  {
    id: 'halifax',
    name: 'Halifax',
    province: 'NS',
    houseCost: 426290,
    monthlyRent: 1826,
    propertyTaxPercent: 1.13,
    condoFees: ESTIMATED_CONDO_FEES
  },
  {
    id: 'winnipeg',
    name: 'Winnipeg',
    province: 'MB',
    houseCost: 287877,
    monthlyRent: 1571,
    // Manitoba taxes a portioned value: the published 2.9366% mill rate applies to 45% of market
    // value, so the effective rate against the price entered here is 2.9366 x 0.45.
    propertyTaxPercent: 1.32,
    condoFees: ESTIMATED_CONDO_FEES,
    note: 'Winnipeg quotes a 2.94% rate, but Manitoba taxes only 45% of market value, so the real cost is about 1.32% of what you paid.'
  }
];

export const getCityPreset = (id: string): CityPreset | undefined =>
  CITY_PRESETS.find(preset => preset.id === id);
